import React, { useMemo, useState } from 'react'
import { CaretDown, CaretRight, Info, MagnifyingGlass, X } from '@phosphor-icons/react'
import {
  CheckboxV2,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import { Column } from 'src/components/common/export-component/types'
import { CheckState, ExportDataSelectProps } from 'src/components/Reports/types'
import { useLocale } from 'src/lib/hooks/useLocal'

const MAX_VISIBLE_CHIPS = 6

const getLeaves = (column: Column): string[] =>
  column.subcolumns && column.subcolumns.length > 0 ? column.subcolumns.flatMap(getLeaves) : [column.name]

const buildTitleMap = (columns: Column[], map: Record<string, string> = {}): Record<string, string> => {
  columns.forEach(col => {
    if (col.subcolumns && col.subcolumns.length > 0) buildTitleMap(col.subcolumns, map)
    else map[col.name] = col.title
  })
  return map
}

// Info tooltip shown next to a field/group with a description.
const FieldInfo: React.FC<{ description?: string }> = ({ description }) => {
  if (!description) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex shrink-0' onClick={e => e.stopPropagation()}>
          <Icon IconComponent={Info} size='xs' className='text-zinc-400' iconProps={{ weight: 'regular' }} />
        </span>
      </TooltipTrigger>
      <TooltipContent align='start'>{description}</TooltipContent>
    </Tooltip>
  )
}

const ExportDataSelect: React.FC<ExportDataSelectProps> = ({ columns, value, onChange, isLoading }) => {
  const { t } = useLocale()
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const selected = useMemo(() => new Set(value), [value])
  const titleMap = useMemo(() => buildTitleMap(columns), [columns])
  const allLeaves = useMemo(() => columns.flatMap(getLeaves), [columns])
  const allChecked = allLeaves.length > 0 && allLeaves.every(leaf => selected.has(leaf))

  const groupState = (column: Column): CheckState => {
    const leaves = getLeaves(column)
    const on = leaves.filter(leaf => selected.has(leaf)).length
    if (on === 0) return 'unchecked'
    if (on === leaves.length) return 'checked'
    return 'indeterminate'
  }

  const setLeaves = (leaves: string[], on: boolean) => {
    const next = new Set(selected)
    leaves.forEach(leaf => (on ? next.add(leaf) : next.delete(leaf)))
    onChange(Array.from(next))
  }

  const toggleGroup = (column: Column) => {
    const leaves = getLeaves(column)
    setLeaves(leaves, !leaves.every(leaf => selected.has(leaf)))
  }

  const toggleExpand = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const query = search.trim().toLowerCase()
  const matches = (col: Column): boolean =>
    col.title.toLowerCase().includes(query) || (col.subcolumns ?? []).some(matches)
  const visibleColumns = query ? columns.filter(matches) : columns

  const selectedChips = value.map(name => ({ name, label: titleMap[name] ?? name }))

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <button
          type='button'
          className='select-outline flex min-h-[42px] w-full items-center gap-2 rounded-lg bg-white px-3 py-2 text-left outline-none transition-all duration-150 hover:input-outline-hover active:input-outline-active focus:input-outline-focus data-[state=open]:input-outline-focus'
        >
          <div className='flex flex-1 flex-wrap items-center gap-1.5'>
            {selectedChips.length === 0 ? (
              <span className='text-text-sm-regular text-zinc-400'>
                {isLoading ? 'Loading fields…' : 'Select fields to export'}
              </span>
            ) : (
              <>
                {selectedChips.slice(0, MAX_VISIBLE_CHIPS).map(chip => (
                  <span
                    key={chip.name}
                    className='inline-flex items-center gap-1 rounded-sm bg-zinc-100 px-1.5 py-0.5 text-text-xs-medium text-zinc-600'
                  >
                    {chip.label}
                    <span
                      role='button'
                      className='inline-flex cursor-pointer'
                      onPointerDown={(e: React.PointerEvent) => {
                        // Radix opens the popover on pointer-down — stop it so removing a chip only deletes.
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setLeaves([chip.name], false)
                      }}
                    >
                      <Icon IconComponent={X} size='xxs' className='text-zinc-400 hover:text-zinc-600' />
                    </span>
                  </span>
                ))}
                {selectedChips.length > MAX_VISIBLE_CHIPS && (
                  // Tag-style affordance — clicking it (like the rest of the trigger) opens the full list.
                  <span className='inline-flex items-center rounded-sm bg-zinc-100 px-1.5 py-0.5 text-text-xs-medium text-zinc-500 transition-colors hover:bg-zinc-200 hover:text-zinc-700'>
                    +{selectedChips.length - MAX_VISIBLE_CHIPS} more
                  </span>
                )}
              </>
            )}
          </div>
          <span className='flex h-[22px] shrink-0 items-center self-start'>
            <Icon IconComponent={CaretDown} size='sm' className='text-zinc-400' />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={4}
        className='w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-xl p-0'
      >
        {/* Search — sized/padded to match the "Share with" MultiSelect search. */}
        <div className='flex flex-shrink-0 items-center gap-2 border-b-[0.5px] border-zinc-200 bg-zinc-50 py-1.5 pl-3.5 pr-3'>
          <Icon IconComponent={MagnifyingGlass} size='sm' className='flex-shrink-0 text-zinc-500' />
          <input
            type='text'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search...'
            className='min-w-0 flex-1 border-0 bg-transparent text-text-sm-regular text-zinc-800 outline-none placeholder:text-[#9F9FA9]'
          />
        </div>

        <div className='max-h-[280px] overflow-y-auto overscroll-contain px-1.5 py-1'>
          {!query && (
            <button
              type='button'
              onClick={() => onChange(allChecked ? [] : allLeaves)}
              className='flex w-full items-center gap-2 rounded-md py-1.5 pl-2 pr-1.5 text-text-sm-medium text-zinc-500 transition-colors hover:bg-zinc-100'
            >
              <CheckboxV2
                className='pointer-events-none shrink-0'
                checked={allChecked}
                indeterminate={!allChecked && selected.size > 0}
                size='sm'
                tabIndex={-1}
                aria-hidden
              />
              Select all
            </button>
          )}

          {visibleColumns.map(column => {
            const hasChildren = !!column.subcolumns?.length
            const isExpanded = expanded.has(column.name) || !!query
            const state = hasChildren ? groupState(column) : selected.has(column.name) ? 'checked' : 'unchecked'
            return (
              <div key={column.name}>
                {/* Whole row toggles: a group expands its submenu, a leaf toggles selection. */}
                <div
                  className='flex w-full cursor-pointer items-center gap-2 rounded-md py-1.5 pl-2 pr-1.5 transition-colors hover:bg-zinc-100'
                  onClick={() =>
                    hasChildren ? toggleExpand(column.name) : setLeaves([column.name], !selected.has(column.name))
                  }
                >
                  {hasChildren ? (
                    <span
                      className='shrink-0'
                      onClick={e => {
                        e.stopPropagation()
                        toggleGroup(column)
                      }}
                    >
                      <CheckboxV2
                        className='pointer-events-none'
                        checked={state === 'checked'}
                        indeterminate={state === 'indeterminate'}
                        size='sm'
                        tabIndex={-1}
                        aria-hidden
                      />
                    </span>
                  ) : (
                    <CheckboxV2
                      className='pointer-events-none shrink-0'
                      checked={state === 'checked'}
                      size='sm'
                      tabIndex={-1}
                      aria-hidden
                    />
                  )}
                  <span className='text-text-sm-regular text-zinc-500'>{column.title}</span>
                  {hasChildren && (
                    <span className='inline-flex min-w-[20px] items-center justify-center rounded-full bg-zinc-100 px-1.5 py-0.5 text-text-xs-medium text-zinc-600'>
                      {column.subcolumns?.length}
                    </span>
                  )}
                  <FieldInfo description={column.description} />
                  {hasChildren && (
                    <Icon
                      IconComponent={isExpanded ? CaretDown : CaretRight}
                      size='xs'
                      className='ml-auto shrink-0 text-zinc-400'
                    />
                  )}
                </div>

                {hasChildren && isExpanded && (
                  <div className='pl-6'>
                    {column.subcolumns
                      ?.filter(child => (query ? child.title.toLowerCase().includes(query) : true))
                      .map(child => (
                        <button
                          key={child.name}
                          type='button'
                          onClick={() => setLeaves([child.name], !selected.has(child.name))}
                          className='flex w-full items-center gap-2 rounded-md py-1.5 pl-2 pr-1.5 text-left text-text-sm-regular text-zinc-600 transition-colors hover:bg-zinc-100'
                        >
                          <CheckboxV2
                            className='pointer-events-none shrink-0'
                            checked={selected.has(child.name)}
                            size='sm'
                            tabIndex={-1}
                            aria-hidden
                          />
                          {child.title}
                          <FieldInfo description={child.description} />
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )
          })}

          {query && visibleColumns.length === 0 && (
            <p className='py-8 text-center text-text-sm-regular text-zinc-400'>{t('report.dialog.no_matching_results')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ExportDataSelect
